import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppState } from '@/store';
import { showSuccessToast } from '@/components';
import { Resource, usePermissions } from '@/modules/Permissions';
import { getCategoryCommissions, updateCategoryCommissions } from '@/modules/Commission/feature';
import { commissionSelector } from '@/modules/Commission/feature/selectors';
import {
  buildEditedValuesFromTree,
  collectCategoryAndDescendantIds,
  findCategoryInTree,
  flattenCategoryCommissionTree,
} from '@/modules/Commission/helpers';
import { isCommissionValueValid } from '@/modules/Commission/helpers/commissionValidation';

export const useCommissionCategoriesTab = () => {
  const { t } = useTranslation(['commission']);
  const dispatch = useAppDispatch();
  const { canManage } = usePermissions();
  const { categories, isLoading, isSaving } = useAppState(commissionSelector);
  const [editedValues, setEditedValues] = useState<Record<string, number>>({});

  const flatCategories = useMemo(() => flattenCategoryCommissionTree(categories), [categories]);

  const loadCommissions = useCallback(async () => {
    try {
      const response = await dispatch(getCategoryCommissions()).unwrap();
      setEditedValues(buildEditedValuesFromTree(response.data));
    } catch (error) {
      console.error(error);
    }
  }, [dispatch]);

  useEffect(() => {
    loadCommissions();
  }, [loadCommissions]);

  const changedItems = useMemo(
    () =>
      flatCategories
        .filter((category) => editedValues[category.id] !== category.commission_percent)
        .map((category) => ({
          id: category.id,
          commission_percent: editedValues[category.id],
        })),
    [flatCategories, editedValues],
  );

  const hasInvalidValues = useMemo(
    () => Object.values(editedValues).some((value) => !isCommissionValueValid(value)),
    [editedValues],
  );

  const handleCommissionChange = (categoryId: string, value: number | null) => {
    if (value === null) {
      return;
    }

    const category = findCategoryInTree(categories, categoryId);
    const idsToUpdate = category ? collectCategoryAndDescendantIds(category) : [categoryId];

    setEditedValues((prev) => {
      const next = { ...prev };

      idsToUpdate.forEach((id) => {
        next[id] = value;
      });

      return next;
    });
  };

  const handleSave = async () => {
    if (changedItems.length === 0) {
      showSuccessToast({ message: t('no_changes') });
      return;
    }

    if (hasInvalidValues) {
      return;
    }

    try {
      await dispatch(updateCategoryCommissions({ items: changedItems })).unwrap();
      showSuccessToast({ message: t('save_success') });
      await loadCommissions();
    } catch (error) {
      console.error(error);
    }
  };

  return {
    categories,
    editedValues,
    handleCommissionChange,
    isLoading,
    isSaving,
    hasInvalidValues,
    canSave: canManage(Resource.COMMISSION),
    canManage: canManage(Resource.COMMISSION),
    handleSave,
  };
};
